import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { useAuth } from "../Auth-Context/AuthContext";
import {
  getContactsKanban,
  updateContactStage,
  getInteractionsByContact,
  logInteraction,
  closeDeal,
  updateContactQualification,
} from "../api/services";
import ContactModal from "../components/ContactModal";
import DealFormModal from "../components/DealFormModal";
import {
  X,
  Phone,
  Users,
  ArrowRight,
  MoreHorizontal,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronRight,
  Briefcase,
  Plus,
  CheckCircle,
  XCircle,
  Check,
  AlertTriangle,
} from "lucide-react";

const STAGES = [
  {
    id: "prospecting",
    name: "Prospecting",
    color: "#8B5CF6",
    status: "PROSPECTING",
  },
  {
    id: "lead_qualification",
    name: "Lead Qualification",
    color: "#EC4899",
    status: "LEAD_QUALIFICATION",
  },
  {
    id: "discovery_pain_points",
    name: "Discovery/Pain Points",
    color: "#F59E0B",
    status: "DISCOVERY_PAIN_POINTS",
  },
  {
    id: "demo_pitch",
    name: "Demo/Pitch",
    color: "#10B981",
    status: "DEMO_PITCH",
  },
  {
    id: "proposal",
    name: "Proposal",
    color: "#3B82F6",
    status: "PROPOSAL",
  },
  {
    id: "negotiation",
    name: "Negotiation",
    color: "#6366F1",
    status: "NEGOTIATION",
  },
  {
    id: "closed",
    name: "Closed",
    color: "#059689",
    status: "CLOSED",
  },
  {
    id: "customer",
    name: "Customer",
    color: "#009DB9",
    status: "CUSTOMER",
  },
];

// Dormant stages
const DORMANT_STAGES = [
  "LEAD_QUALIFICATION",
  "DISCOVERY_PAIN_POINTS",
  "DEMO_PITCH",
  "PROPOSAL",
];

const DORMANT_LIGHT_THRESHOLD_MS = 3 * 24 * 60 * 60 * 1000;
const DORMANT_STRONG_THRESHOLD_MS = 5 * 24 * 60 * 60 * 1000;

const NOTE_CATEGORY_QUALIFICATION = "QUALIFICATION RESULT";

// ── Close Deal Modal ─────────────────────────────────────────────────────────
function CloseDealModal({
  open,
  onClose,
  deal,
  onCloseDeal,
  dealTitle,
}) {
  const [outcome, setOutcome] = useState("CLOSED_WON");
  const [dealAmount, setDealAmount] = useState(
    deal?.value > 0 ? deal.value : ""
  );
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open && deal) {
      setOutcome("CLOSED_WON");
      setDealAmount(deal.value > 0 ? deal.value : "");
      setNote("");
      setError("");
    }
  }, [open, deal]);

  if (!open || !deal) return null;

  const handleSubmit = async () => {
    if (!outcome) {
      setError("Please select Won or Lost");
      return;
    }

    if (
      outcome === "CLOSED_WON" &&
      (!dealAmount || parseFloat(dealAmount) <= 0)
    ) {
      setError("Please enter a valid deal amount (must be greater than 0)");
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const payload = { outcome };

      if (outcome === "CLOSED_WON") {
        payload.dealAmount = parseFloat(dealAmount);
      }

      if (note && note.trim()) {
        payload.note = note.trim();
      }

      await onCloseDeal(deal.id, payload);
      onClose();
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to close deal"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="flex flex-col w-full max-w-md mx-4 bg-white shadow-2xl rounded-2xl">
        <div className="flex items-center justify-between px-6 py-5 border-b border-neutral-100">
          <div>
            <h3 className="text-lg font-bold text-neutral-900">
              Close Deal
            </h3>
            <p className="mt-0.5 text-xs text-neutral-500">
              {dealTitle}
            </p>
          </div>

          <button
            onClick={onClose}
            className="transition-colors text-neutral-400 hover:text-neutral-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col gap-5 p-6">
          {error && (
            <div className="p-3 text-sm text-red-600 border border-red-200 rounded-lg bg-red-50">
              {error}
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold tracking-wider uppercase text-neutral-500">
              Outcome *
            </label>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setOutcome("CLOSED_WON")}
                className={`flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-xl border-2 transition-all ${
                  outcome === "CLOSED_WON"
                    ? "border-emerald-500 bg-emerald-50"
                    : "border-neutral-200 hover:border-emerald-300"
                }`}
              >
                <CheckCircle
                  size={24}
                  className={
                    outcome === "CLOSED_WON"
                      ? "text-emerald-600"
                      : "text-neutral-400"
                  }
                />

                <span
                  className={`font-bold ${
                    outcome === "CLOSED_WON"
                      ? "text-emerald-700"
                      : "text-neutral-600"
                  }`}
                >
                  WON
                </span>
              </button>

              <button
                onClick={() => setOutcome("CLOSED_LOST")}
                className={`flex flex-col items-center justify-center gap-2 px-4 py-4 rounded-xl border-2 transition-all ${
                  outcome === "CLOSED_LOST"
                    ? "border-red-500 bg-red-50"
                    : "border-neutral-200 hover:border-red-300"
                }`}
              >
                <XCircle
                  size={24}
                  className={
                    outcome === "CLOSED_LOST"
                      ? "text-red-600"
                      : "text-neutral-400"
                  }
                />

                <span
                  className={`font-bold ${
                    outcome === "CLOSED_LOST"
                      ? "text-red-700"
                      : "text-neutral-600"
                  }`}
                >
                  LOST
                </span>
              </button>
            </div>
          </div>

          {outcome === "CLOSED_WON" && (
            <div className="flex flex-col gap-2">
              <label className="text-xs font-semibold tracking-wider uppercase text-neutral-500">
                Deal Amount *
              </label>

              <div className="relative">
                <DollarSign
                  size={16}
                  className="absolute left-3 top-2.5 text-neutral-400"
                />

                <input
                  type="number"
                  value={dealAmount}
                  onChange={(e) => setDealAmount(e.target.value)}
                  placeholder="Enter final deal amount"
                  className="w-full h-10 pl-9 pr-4 text-sm border border-neutral-200 rounded-lg outline-none focus:border-[#009DB9] focus:ring-1 focus:ring-[#009DB9]"
                  autoFocus
                />
              </div>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <label className="text-xs font-semibold tracking-wider uppercase text-neutral-500">
              {outcome === "CLOSED_WON"
                ? "Win Note (Optional)"
                : "Loss Reason (Optional)"}
            </label>

            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={
                outcome === "CLOSED_WON"
                  ? "What made this deal successful?"
                  : "Why was this deal lost?"
              }
              className="w-full px-3 py-2 text-sm border border-neutral-200 rounded-lg outline-none resize-none focus:border-[#009DB9] focus:ring-1 focus:ring-[#009DB9]"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-100 bg-neutral-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium text-neutral-600 rounded-xl hover:bg-neutral-200"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`px-6 py-2 text-sm font-semibold text-white rounded-xl disabled:opacity-50 transition-all ${
              outcome === "CLOSED_WON"
                ? "bg-emerald-600 hover:bg-emerald-700"
                : "bg-red-600 hover:bg-red-700"
            }`}
          >
            {submitting
              ? "Closing..."
              : `Confirm ${
                  outcome === "CLOSED_WON" ? "Won" : "Lost"
                }`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Verification Modal ───────────────────────────────────────────────────────
function VerificationModal({
  open,
  onClose,
  contact,
  onDecision,
  submitting,
  note,
  onNoteChange,
}) {
  if (!open || !contact) return null;

  const companyName =
    contact.company ||
    contact.company_name ||
    "this company";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) {
          onClose();
        }
      }}
    >
      {/* Compact modal */}
      <div className="w-full max-w-md overflow-hidden bg-white border shadow-xl border-neutral-200 rounded-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
          <div>
            <h3 className="text-base font-semibold text-neutral-900">
              Verify Company
            </h3>

            <p className="mt-0.5 text-xs text-neutral-500">
              Review the company and record your qualification decision.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="flex items-center justify-center w-8 h-8 transition-colors rounded-lg text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 space-y-5">
          {/* Company */}
          <div className="p-3 border rounded-lg border-neutral-200 bg-neutral-50">
            <p className="mb-1 text-[10px] font-semibold tracking-wider uppercase text-neutral-400">
              Company
            </p>

            <p className="text-sm font-semibold truncate text-neutral-900">
              {companyName}
            </p>
          </div>

          {/* Research Note */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label
                htmlFor="verification-research-note"
                className="text-xs font-semibold tracking-wide text-neutral-700"
              >
                Research Note
                <span className="ml-1 font-normal text-neutral-400">
                  (Optional)
                </span>
              </label>
            </div>

            <textarea
              id="verification-research-note"
              value={note}
              onChange={(e) => onNoteChange(e.target.value)}
              placeholder="Add any findings or notes from your research..."
              rows={5}
              disabled={submitting}
              className="w-full px-3 py-2.5 text-sm text-neutral-800 placeholder:text-neutral-400 border border-neutral-200 rounded-lg outline-none resize-y transition-colors focus:border-[#009DB9] focus:ring-2 focus:ring-[#009DB9]/10 disabled:bg-neutral-50 disabled:cursor-not-allowed"
            />
          </div>

          {/* Decision */}
          <div className="space-y-2">
            <p className="text-xs font-semibold tracking-wide text-neutral-700">
              Qualification Decision
            </p>

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => onDecision(true)}
                disabled={submitting}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-emerald-700 transition-all border border-emerald-200 rounded-lg bg-emerald-50 hover:border-emerald-400 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircle size={16} />
                Qualified
              </button>

              <button
                type="button"
                onClick={() => onDecision(false)}
                disabled={submitting}
                className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-orange-700 transition-all border border-orange-200 rounded-lg bg-orange-50 hover:border-orange-400 hover:bg-orange-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <XCircle size={16} />
                Not Qualified
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end px-5 py-3.5 border-t border-neutral-100 bg-neutral-50">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm font-medium transition-colors rounded-lg text-neutral-600 hover:bg-neutral-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Nested deal row ──────────────────────────────────────────────────────────
function DealRow({ deal, onCloseDealClick }) {
  const isWon = deal.stage === "CLOSED_WON";
  const isLost = deal.stage === "CLOSED_LOST";
  const isNegotiation = deal.stage === "NEGOTIATION";

  const stageColors = {
    PROSPECTING: { color: "#8B5CF6", bg: "#ede9fe" },
    LEAD_QUALIFICATION: { color: "#EC4899", bg: "#fce7f3" },
    DISCOVERY_PAIN_POINTS: { color: "#F59E0B", bg: "#fef3c7" },
    DEMO_PITCH: { color: "#10B981", bg: "#d1fae5" },
    PROPOSAL: { color: "#3B82F6", bg: "#dbeafe" },
    NEGOTIATION: { color: "#6366F1", bg: "#e0e7ff" },
    CLOSED_WON: { color: "#059669", bg: "#d1fae5" },
    CLOSED_LOST: { color: "#EF4444", bg: "#fee2e2" },
    CUSTOMER: { color: "#009DB9", bg: "#e0f7fa" },
  };

  const s =
    stageColors[deal.stage] || {
      color: "#6b7280",
      bg: "#f3f4f6",
    };

  return (
    <div className="flex items-center justify-between gap-2 px-2 py-1.5 rounded-lg bg-white border border-neutral-100">
      <div className="flex items-center flex-1 min-w-0 gap-1.5">
        <span
          className="w-1.5 h-1.5 rounded-full flex-shrink-0"
          style={{ background: s.color }}
        />

        <span className="text-[11px] font-medium text-neutral-700 truncate">
          {deal.title}
        </span>
      </div>

      <div className="flex items-center flex-shrink-0 gap-2">
        {deal.value ? (
          <span
            className={`text-[10px] font-bold ${
              isWon
                ? "text-emerald-600"
                : isLost
                ? "text-red-600"
                : "text-neutral-500"
            }`}
          >
            {deal.currency || ""}{" "}
            {Number(deal.value).toLocaleString()}
          </span>
        ) : null}

        {isNegotiation && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onCloseDealClick(deal);
            }}
            className="px-2 py-1 text-[9px] font-semibold rounded-md transition-all"
            style={{
              background: "#6366F1",
              color: "white",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "#4f46e5")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "#6366F1")
            }
          >
            Close Deal
          </button>
        )}
      </div>
    </div>
  );
}

// ── Contact Card ──────────────────────────────────────────────────────────────
function ContactCard({
  contact,
  onCardClick,
  onMoveClick,
  onAddDeal,
  onCloseDealClick,
  onMoveToCustomer,
  onVerifiedClick,
  isManager,
}) {
  const [dealsOpen, setDealsOpen] = useState(false);

  const stage = STAGES.find(
    (s) => s.status === contact.pipelineStage
  );

  const deals = contact.deals || [];

  const isClosedWon = contact.pipelineStage === "CLOSED_WON";
  const isClosedLost = contact.pipelineStage === "CLOSED_LOST";
  const isClosed = isClosedWon || isClosedLost;
  const isCustomer = contact.pipelineStage === "CUSTOMER";
  const isNegotiation = contact.pipelineStage === "NEGOTIATION";
  const isProspecting = contact.pipelineStage === "PROSPECTING";

  const isNotQualified = contact.qualified === false;

  const getDormancyLevel = () => {
    if (!DORMANT_STAGES.includes(contact.pipelineStage)) {
      return null;
    }

    if (!contact.interactions || contact.interactions.length === 0) {
      return "strong";
    }

    const sortedInteractions = [...contact.interactions].sort(
      (a, b) =>
        new Date(b.occurredAt) - new Date(a.occurredAt)
    );

    const latestInteraction = sortedInteractions[0];

    if (!latestInteraction) {
      return "strong";
    }

    const now = new Date();
    const lastInteractionDate = new Date(
      latestInteraction.occurredAt
    );

    const diffInMs =
      now.getTime() - lastInteractionDate.getTime();

    if (diffInMs >= DORMANT_STRONG_THRESHOLD_MS) {
      return "strong";
    }

    if (diffInMs >= DORMANT_LIGHT_THRESHOLD_MS) {
      return "light";
    }

    return null;
  };

  const dormancyLevel = getDormancyLevel();

  const getInitials = () => {
    if (contact.company) {
      return contact.company.substring(0, 2).toUpperCase();
    }

    if (contact.company_name) {
      return contact.company_name
        .substring(0, 2)
        .toUpperCase();
    }

    if (contact.firstName && contact.lastName) {
      return `${contact.firstName[0]}${contact.lastName[0]}`.toUpperCase();
    }

    if (contact.firstName) {
      return contact.firstName.substring(0, 2).toUpperCase();
    }

    return "CO";
  };

  const getColorFromName = (name) => {
    if (!name) {
      return {
        bg: "#e0f2fe",
        color: "#0284c7",
      };
    }

    const colors = [
      { bg: "#e0f2fe", color: "#0284c7" },
      { bg: "#fce7f3", color: "#db2777" },
      { bg: "#fef3c7", color: "#d97706" },
      { bg: "#d1fae5", color: "#059669" },
      { bg: "#e0e7ff", color: "#4f46e5" },
      { bg: "#fee2e2", color: "#dc2626" },
      { bg: "#f3e8ff", color: "#9333ea" },
      { bg: "#fff3e0", color: "#ea580c" },
    ];

    const index = name.length % colors.length;

    return colors[index];
  };

  const companyName =
    contact.company ||
    contact.company_name ||
    "No Company Name";

  const colorStyle = getColorFromName(companyName);

  const getCardBackground = () => {
    if (isClosedWon) {
      return "bg-emerald-50/30 border-emerald-200";
    }

    if (isClosedLost) {
      return "bg-red-50/30 border-red-200";
    }

    if (isCustomer) {
      return "bg-cyan-50/30";
    }

    if (isNotQualified) {
      return "bg-orange-50/60 border-orange-300";
    }

    if (dormancyLevel === "strong") {
      return "bg-yellow-100/70 border-yellow-400";
    }

    if (dormancyLevel === "light") {
      return "bg-yellow-50/50 border-yellow-300";
    }

    return "bg-white";
  };

  let negotiationDeal = deals.find(
    (d) => d.stage === "NEGOTIATION"
  );

  if (
    isNegotiation &&
    !negotiationDeal &&
    deals.length > 0
  ) {
    negotiationDeal = deals[0];
  }

  const renderActionButton = () => {
    if (isProspecting) {
      if (isManager) {
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onVerifiedClick(contact);
            }}
            className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-white rounded-lg transition-colors"
            style={{
              background: "#10B981",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "#059669")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "#10B981")
            }
          >
            <Check size={9} />
            Verify
          </button>
        );
      }

      return null;
    }

    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onMoveClick(contact);
        }}
        className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold rounded-lg transition-colors"
        style={{
          background: "rgba(0,157,185,0.1)",
          color: "#009DB9",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background =
            "rgba(0,157,185,0.2)")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.background =
            "rgba(0,157,185,0.1)")
        }
      >
        Move
        <ArrowRight size={9} />
      </button>
    );
  };

  const getDormancyBadge = () => {
    if (dormancyLevel === "strong") {
      return "⚠️ No activity for 5+ days";
    }

    if (dormancyLevel === "light") {
      return "⚠️ No activity for 3+ days";
    }

    return null;
  };

  const dormancyBadge = getDormancyBadge();

  return (
    <div
      className={`mb-2 overflow-hidden transition-all duration-150 border rounded-xl hover:shadow-md hover:-translate-y-px ${getCardBackground()}`}
      style={{
        borderColor: isNotQualified
          ? "#fb923c"
          : dormancyLevel === "strong"
          ? "#fbbf24"
          : dormancyLevel === "light"
          ? "#fcd34d"
          : "#e5e5e5",

        boxShadow: isNotQualified
          ? "0 1px 3px rgba(251,146,60,0.25)"
          : dormancyLevel
          ? "0 1px 3px rgba(251,191,36,0.2)"
          : "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      {isNotQualified ? (
        <div className="flex items-center gap-1 px-3 py-1 text-[10px] font-semibold text-orange-800 bg-orange-200/70 border-b border-orange-300">
          <AlertTriangle size={10} />
          Not Qualified
        </div>
      ) : (
        dormancyBadge && (
          <div
            className={`px-3 py-1 text-[10px] font-semibold border-b ${
              dormancyLevel === "strong"
                ? "text-yellow-800 bg-yellow-200/80 border-yellow-400"
                : "text-yellow-700 bg-yellow-100/80 border-yellow-300"
            }`}
          >
            {dormancyBadge}
          </div>
        )
      )}

      <div
        className="flex items-center gap-2 px-3 py-2.5 cursor-pointer"
        onClick={() => onCardClick(contact)}
      >
        <div
          className="flex items-center justify-center flex-shrink-0 w-7 h-7 rounded-lg text-[11px] font-bold"
          style={{
            background: colorStyle.bg,
            color: colorStyle.color,
          }}
        >
          {getInitials()}
        </div>

        <div className="flex-1 min-w-0">
          <span className="block text-[13px] font-semibold text-neutral-900 truncate">
            {companyName}
          </span>

          {(contact.firstName || contact.lastName) && (
            <span className="block text-[10px] text-neutral-400 truncate">
              {contact.firstName} {contact.lastName}
            </span>
          )}
        </div>

        <div onClick={(e) => e.stopPropagation()}>
          <button className="flex items-center justify-center w-6 h-6 rounded text-neutral-400 hover:bg-neutral-100">
            <MoreHorizontal size={13} />
          </button>
        </div>
      </div>

      <div
        className="flex flex-col gap-0.5 px-3 pb-2 cursor-pointer"
        onClick={() => onCardClick(contact)}
      >
        {contact.phone && (
          <div className="flex items-center gap-1.5 py-0.5">
            <Phone
              size={10}
              className="flex-shrink-0 text-neutral-300"
            />

            <span className="text-[11px] text-neutral-500 truncate">
              {contact.phone}
            </span>
          </div>
        )}

        {contact.industry && (
          <div className="flex items-center gap-1.5 py-0.5">
            <Briefcase
              size={10}
              className="flex-shrink-0 text-neutral-300"
            />

            <span className="text-[11px] text-neutral-500 truncate">
              {contact.industry}
            </span>
          </div>
        )}
      </div>

      <div className="px-3 pb-2">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setDealsOpen((v) => !v);
          }}
          className="flex items-center w-full gap-1 py-1 text-left"
        >
          {dealsOpen ? (
            <ChevronDown
              size={11}
              className="text-neutral-400"
            />
          ) : (
            <ChevronRight
              size={11}
              className="text-neutral-400"
            />
          )}

          <span className="text-[10px] font-semibold text-neutral-400">
            {deals.length} deal
            {deals.length !== 1 ? "s" : ""}
          </span>
        </button>

        {dealsOpen && (
          <div className="flex flex-col gap-1 mt-1">
            {deals.length > 0 ? (
              deals.map((deal) => (
                <DealRow
                  key={deal.id}
                  deal={deal}
                  onCloseDealClick={onCloseDealClick}
                />
              ))
            ) : (
              <p className="px-1 py-1 text-[10px] text-neutral-400">
                No deals yet
              </p>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddDeal(contact);
              }}
              className="flex items-center gap-1 px-1 py-1 mt-0.5 text-[10px] font-semibold rounded-lg transition-colors"
              style={{
                color: "#009DB9",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background =
                  "rgba(0,157,185,0.08)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  "transparent")
              }
            >
              <Plus size={10} strokeWidth={2.5} />
              Add Deal
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between px-3 py-2 border-t border-neutral-100 bg-neutral-50/60">
        {isClosed ? (
          <div className="flex items-center justify-between w-full">
            <div
              className={`flex items-center gap-1.5 text-[10px] font-semibold ${
                isClosedWon
                  ? "text-emerald-600"
                  : "text-red-600"
              }`}
            >
              {isClosedWon ? (
                <TrendingUp size={10} />
              ) : (
                <TrendingDown size={10} />
              )}

              {isClosedWon
                ? "Closed Won"
                : "Closed Lost"}
            </div>

            {isClosedWon && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onMoveToCustomer(contact);
                }}
                className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold rounded-lg transition-colors"
                style={{
                  background: "rgba(0,157,185,0.1)",
                  color: "#009DB9",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background =
                    "rgba(0,157,185,0.2)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background =
                    "rgba(0,157,185,0.1)")
                }
              >
                Move to Customer
                <ArrowRight size={9} />
              </button>
            )}
          </div>
        ) : isCustomer ? (
          <div className="flex items-center w-full gap-1.5 text-[10px] font-semibold text-cyan-600">
            <Users size={10} />
            Customer
          </div>
        ) : isNegotiation ? (
          <>
            <span className="text-[10px] text-neutral-400">
              Negotiation
            </span>

            <button
              onClick={(e) => {
                e.stopPropagation();

                if (negotiationDeal) {
                  onCloseDealClick(negotiationDeal);
                } else {
                  alert(
                    "Cannot close deal: no deal associated with this company."
                  );
                }
              }}
              className="flex items-center gap-1 px-2.5 py-1 text-[10px] font-semibold text-white rounded-lg transition-colors cursor-pointer"
              style={{
                background: "#6366F1",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background =
                  "#4f46e5")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background =
                  "#6366F1")
              }
            >
              Close Deal
              <ArrowRight size={9} />
            </button>
          </>
        ) : (
          <>
            <span className="text-[10px] text-neutral-400">
              {stage?.name || contact.pipelineStage}
            </span>

            {renderActionButton()}
          </>
        )}
      </div>
    </div>
  );
}

// ── Move Stage Modal ──────────────────────────────────────────────────────────
function MoveContactStageModal({
  open,
  onClose,
  contact,
  onMove,
}) {
  const [selected, setSelected] = useState(null);

  if (!open || !contact) return null;

  const currentStatus = contact.pipelineStage;

  const getNextStages = (currentStage) => {
    const stageFlow = {
      PROSPECTING: [
        "LEAD_QUALIFICATION",
        "DISCOVERY_PAIN_POINTS",
      ],
      LEAD_QUALIFICATION: [
        "DISCOVERY_PAIN_POINTS",
        "DEMO_PITCH",
      ],
      DISCOVERY_PAIN_POINTS: [
        "DEMO_PITCH",
        "PROPOSAL",
      ],
      DEMO_PITCH: [
        "PROPOSAL",
        "NEGOTIATION",
      ],
      PROPOSAL: [
        "NEGOTIATION",
        "CLOSED",
      ],
      NEGOTIATION: [
        "CLOSED",
        "CUSTOMER",
      ],
      CLOSED: ["CUSTOMER"],
      CUSTOMER: [],
    };

    return stageFlow[currentStage] || [];
  };

  const nextStages = getNextStages(currentStatus);

  let availableStages = STAGES.filter((s) =>
    nextStages.includes(s.status)
  );

  if (
    availableStages.length === 0 &&
    currentStatus !== "CUSTOMER"
  ) {
    if (currentStatus === "CLOSED") {
      availableStages = STAGES.filter(
        (s) => s.status === "CUSTOMER"
      );
    }
  }

  const handleConfirm = async () => {
    if (!selected) return;

    await onMove(contact.id, selected);

    setSelected(null);
    onClose();
  };

  const currentStageObj = STAGES.find(
    (s) => s.status === currentStatus
  );

  const currentStageName =
    currentStageObj?.name || currentStatus;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          setSelected(null);
          onClose();
        }
      }}
    >
      <div className="flex flex-col w-full max-w-md mx-4 bg-white shadow-2xl rounded-2xl">
        <div className="flex items-center justify-between px-6 py-5 border-b border-neutral-100">
          <h3 className="text-base font-bold">
            Move Stage —{" "}
            {contact.company ||
              contact.company_name ||
              "Company"}
          </h3>

          <button
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="flex flex-col gap-4 p-6">
          <p className="text-sm text-neutral-600">
            Currently:{" "}
            <strong>{currentStageName}</strong>
          </p>

          <div className="space-y-2">
            <p className="text-xs font-semibold tracking-wider uppercase text-neutral-500">
              Available Next Stages
            </p>

            {availableStages.length === 0 ? (
              <p className="py-4 text-sm text-center text-neutral-400">
                This company is at the final stage. No further
                moves available.
              </p>
            ) : (
              availableStages.map((stage) => (
                <button
                  key={stage.status}
                  onClick={() =>
                    setSelected(stage.status)
                  }
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-all text-left ${
                    selected === stage.status
                      ? "border-2"
                      : "border-neutral-200"
                  }`}
                  style={
                    selected === stage.status
                      ? {
                          borderColor: stage.color,
                          backgroundColor: `${stage.color}10`,
                        }
                      : {}
                  }
                >
                  <div
                    className="flex-shrink-0 w-3 h-3 rounded-full"
                    style={{
                      background: stage.color,
                    }}
                  />

                  <span className="font-medium">
                    {stage.name}
                  </span>

                  {selected === stage.status && (
                    <span className="ml-auto text-xs text-green-600">
                      ✓ Selected
                    </span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-neutral-100">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm font-medium text-neutral-600 rounded-xl hover:bg-neutral-100"
          >
            Cancel
          </button>

          <button
            onClick={handleConfirm}
            disabled={!selected}
            className="px-6 py-2 text-sm font-semibold text-white rounded-xl disabled:opacity-50"
            style={{
              background: "#009DB9",
            }}
          >
            Move Stage
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Pipeline Component ───────────────────────────────────────────────────
export default function Pipeline() {
  const { user } = useAuth();

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStage] = useState("all");
  const [searchVal, setSearchVal] = useState("");

  const [moveModal, setMoveModal] = useState({
    open: false,
    contact: null,
  });

  const [contactModal, setContactModal] = useState({
    open: false,
    contact: null,
  });

  const [dealFormModal, setDealFormModal] = useState({
    open: false,
    contact: null,
    deal: null,
    onSuccess: null,
  });

  const [closeDealModal, setCloseDealModal] = useState({
    open: false,
    deal: null,
    dealTitle: "",
  });

  const [interactions, setInteractions] = useState([]);
  const [loadingInteractions, setLoadingInteractions] =
    useState(false);

  const [verificationModal, setVerificationModal] =
    useState({
      open: false,
      contact: null,
    });

  const [verifyingSubmitting, setVerifyingSubmitting] =
    useState(false);

  const [verificationNote, setVerificationNote] =
    useState("");

  const isManager = user?.role === "MANAGER";

  const getDisplayStage = (pipelineStage) => {
    if (
      pipelineStage === "CLOSED_WON" ||
      pipelineStage === "CLOSED_LOST"
    ) {
      return "CLOSED";
    }

    return pipelineStage;
  };

  const fetchContacts = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getContactsKanban();

      const list = Array.isArray(data)
        ? data
        : Object.values(data).flat();

      const contactsWithInteractions = await Promise.all(
        list.map(async (contact) => {
          try {
            const interactionsData =
              await getInteractionsByContact(contact.id);

            const interactionList = Array.isArray(
              interactionsData
            )
              ? interactionsData
              : interactionsData.content || [];

            return {
              ...contact,
              interactions: interactionList,
            };
          } catch (err) {
            console.error(
              `Failed to fetch interactions for contact ${contact.id}:`,
              err
            );

            return {
              ...contact,
              interactions: [],
            };
          }
        })
      );

      const sorted = contactsWithInteractions.sort(
        (a, b) => {
          const displayA = getDisplayStage(
            a.pipelineStage
          );

          const displayB = getDisplayStage(
            b.pipelineStage
          );

          const iA = STAGES.findIndex(
            (s) => s.status === displayA
          );

          const iB = STAGES.findIndex(
            (s) => s.status === displayB
          );

          return iA - iB;
        }
      );

      setContacts(sorted);
    } catch (err) {
      console.error(
        "Failed to fetch contacts kanban:",
        err
      );

      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInteractionsForContact = useCallback(
    async (contactId) => {
      setLoadingInteractions(true);

      try {
        const res =
          await getInteractionsByContact(contactId);

        const interactionList = Array.isArray(res)
          ? res
          : res.content || [];

        setInteractions(interactionList);
      } catch (err) {
        console.error(
          "Failed to fetch interactions:",
          err
        );

        setInteractions([]);
      } finally {
        setLoadingInteractions(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleMoveContactStage = async (
    contactId,
    newStage
  ) => {
    try {
      await updateContactStage(
        contactId,
        newStage
      );

      await fetchContacts();
    } catch (err) {
      console.error(
        "Failed to move contact stage:",
        err
      );

      alert(
        err.response?.data?.message ||
          "Failed to update stage"
      );
    }
  };

  const handleVerifiedClick = (contact) => {
    setVerificationNote("");

    setVerificationModal({
      open: true,
      contact,
    });
  };

  const handleCloseVerification = () => {
    if (verifyingSubmitting) return;

    setVerificationModal({
      open: false,
      contact: null,
    });

    setVerificationNote("");
  };

  const handleQualificationDecision = async (
    decision
  ) => {
    const contact = verificationModal.contact;

    if (!contact) return;

    setVerifyingSubmitting(true);

    try {
      await updateContactQualification({
        id: contact.id,
        decision: decision
          ? "QUALIFIED"
          : "NOT_QUALIFIED",
        note: null,
      });

      try {
        await logInteraction({
          type: "NOTE",
          contactId: contact.id,
          contactName:
            `${contact.firstName || ""} ${
              contact.lastName || ""
            }`.trim() ||
            contact.company ||
            contact.company_name ||
            "",
          subject: `${NOTE_CATEGORY_QUALIFICATION} - ${
            decision
              ? "Qualified"
              : "Not Qualified"
          }`,
          notes: verificationNote,
          occurredAt: new Date().toISOString(),
        });
      } catch (logErr) {
        console.error(
          "Failed to log verification note:",
          logErr
        );
      }

      if (decision === true) {
        await updateContactStage(
          contact.id,
          "LEAD_QUALIFICATION"
        );
      }

      await fetchContacts();

      setVerificationModal({
        open: false,
        contact: null,
      });

      setVerificationNote("");
    } catch (err) {
      console.error(
        "Failed to update qualification:",
        err
      );

      alert(
        err.response?.data?.message ||
          "Failed to update qualification status"
      );
    } finally {
      setVerifyingSubmitting(false);
    }
  };

  const handleCloseDeal = async (
    dealId,
    closeData,
    contactId
  ) => {
    try {
      await closeDeal(dealId, closeData);

      await updateContactStage(
        contactId,
        closeData.outcome
      );

      await fetchContacts();
    } catch (err) {
      console.error(
        "Failed to close deal:",
        err
      );

      throw err;
    }
  };

  const handleCardClick = async (contact) => {
    setContactModal({
      open: true,
      contact,
    });

    await fetchInteractionsForContact(
      contact.id
    );
  };

  const handleOpenDealForm = ({
    contact,
    deal,
    onSuccess,
  }) => {
    setDealFormModal({
      open: true,
      contact,
      deal,
      onSuccess: async () => {
        if (onSuccess) {
          await onSuccess();
        }

        await fetchContacts();
      },
    });
  };

  const handleRefreshInteractions = async () => {
    if (contactModal.contact?.id) {
      await fetchInteractionsForContact(
        contactModal.contact.id
      );
    }
  };

  const filtered = contacts.filter(
    (c) =>
      !searchVal ||
      c.company
        ?.toLowerCase()
        .includes(searchVal.toLowerCase()) ||
      c.company_name
        ?.toLowerCase()
        .includes(searchVal.toLowerCase()) ||
      `${c.firstName} ${c.lastName}`
        .toLowerCase()
        .includes(searchVal.toLowerCase())
  );

  const byStage = (status) => {
    if (status === "CLOSED") {
      return filtered.filter(
        (c) =>
          c.pipelineStage === "CLOSED_WON" ||
          c.pipelineStage === "CLOSED_LOST"
      );
    }

    return filtered.filter(
      (c) => c.pipelineStage === status
    );
  };

  const visibleStages =
    activeStage === "all"
      ? [...STAGES]
      : STAGES.filter(
          (s) => s.status === activeStage
        );

  if (loading) {
    return (
      <div className="flex h-screen overflow-hidden bg-neutral-50">
        <Sidebar />

        <div className="flex flex-col flex-1 overflow-hidden">
          <Topbar
            title="Sales Pipeline"
            subtitle="Loading..."
          />

          <div className="flex items-center justify-center flex-1">
            <div className="w-8 h-8 border-b-2 rounded-full animate-spin border-[#009DB9]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-neutral-50 md:flex-row">
      <Sidebar />

      <div className="flex flex-col flex-1 overflow-hidden">
        <Topbar
          title="Sales Pipeline"
          subtitle={`${contacts.length} companies`}
          search={{
            value: searchVal,
            onChange: setSearchVal,
            placeholder: "Search companies…",
          }}
        />

        <div className="flex-1 overflow-x-auto overflow-y-hidden">
          <div
            className="flex h-full"
            style={{
              minWidth: `${
                visibleStages.length *
                (window.innerWidth < 640
                  ? 240
                  : 264)
              }px`,
            }}
          >
            {visibleStages.map((stage) => {
              const col = byStage(stage.status);

              return (
                <div
                  key={stage.id}
                  className="flex flex-col flex-shrink-0 border-r border-neutral-100"
                  style={{
                    width:
                      window.innerWidth < 640
                        ? "240px"
                        : "264px",
                  }}
                >
                  <div className="flex items-center flex-shrink-0 gap-2 px-3 py-3 bg-white border-b border-neutral-100">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{
                        background: stage.color,
                      }}
                    />

                    <span className="flex-1 text-[11px] md:text-[12px] font-bold text-neutral-700">
                      {stage.name}
                    </span>

                    <span className="text-[10px] md:text-[11px] text-neutral-400">
                      {col.length}
                    </span>
                  </div>

                  <div className="flex-1 p-2 pb-4 overflow-y-auto bg-neutral-50/60">
                    {col.map((contact) => (
                      <ContactCard
                        key={contact.id}
                        contact={contact}
                        onCardClick={handleCardClick}
                        onMoveClick={(c) =>
                          setMoveModal({
                            open: true,
                            contact: c,
                          })
                        }
                        onAddDeal={(c) =>
                          setDealFormModal({
                            open: true,
                            contact: c,
                            deal: null,
                          })
                        }
                        onCloseDealClick={(deal) =>
                          setCloseDealModal({
                            open: true,
                            deal,
                            dealTitle: deal.title,
                          })
                        }
                        onMoveToCustomer={(c) =>
                          handleMoveContactStage(
                            c.id,
                            "CUSTOMER"
                          )
                        }
                        onVerifiedClick={
                          handleVerifiedClick
                        }
                        isManager={isManager}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Verification / Qualification Modal */}
      <VerificationModal
        open={verificationModal.open}
        contact={verificationModal.contact}
        onClose={handleCloseVerification}
        onDecision={handleQualificationDecision}
        submitting={verifyingSubmitting}
        note={verificationNote}
        onNoteChange={setVerificationNote}
      />

      {/* Move Stage Modal */}
      <MoveContactStageModal
        open={moveModal.open}
        contact={moveModal.contact}
        onClose={() =>
          setMoveModal({
            open: false,
            contact: null,
          })
        }
        onMove={handleMoveContactStage}
      />

      {/* Contact Modal */}
      <ContactModal
        open={contactModal.open}
        contact={contactModal.contact}
        onClose={() =>
          setContactModal({
            open: false,
            contact: null,
          })
        }
        onOpenDealForm={handleOpenDealForm}
        interactions={interactions}
        loadingInteractions={loadingInteractions}
        onRefreshInteractions={
          handleRefreshInteractions
        }
      />

      {/* Deal Form Modal */}
      <DealFormModal
        open={dealFormModal.open}
        contact={dealFormModal.contact}
        deal={dealFormModal.deal}
        onClose={() =>
          setDealFormModal({
            open: false,
            contact: null,
            deal: null,
          })
        }
        onSuccess={
          dealFormModal.onSuccess ||
          (async () => {
            await fetchContacts();
          })
        }
      />

      {/* Close Deal Modal */}
      <CloseDealModal
        open={closeDealModal.open}
        deal={closeDealModal.deal}
        dealTitle={closeDealModal.dealTitle}
        onClose={() =>
          setCloseDealModal({
            open: false,
            deal: null,
            dealTitle: "",
          })
        }
        onCloseDeal={(dealId, closeData) => {
          const contactId =
            closeDealModal.deal?.contactId;

          if (!contactId) {
            console.error(
              "No contactId found for deal",
              closeDealModal.deal
            );

            return;
          }

          return handleCloseDeal(
            dealId,
            closeData,
            contactId
          );
        }}
      />
    </div>
  );
}